import SelectToken from '@components/select-token';
import { type FC, memo, useCallback, type ChangeEventHandler, useState, useEffect } from 'react';
import styles from './swap-section.module.scss';
import Input from '@ui/input';
import { Decimal } from 'parrot-sui-sdk';
import { bigNumberToReadable } from '@utils/big-number-to-readable';
import { filterPositiveNumber } from '@utils/filter-positive-number';
import CoinLogo from '@ui/coin-logo';
import downIcon from '@assets/images/header/down.svg';

import { ParrotCoinStruct } from 'src/types';
import { useParrotBalance } from '@hooks/use-parrot-balance';
import { scaleDown } from '@utils/math';
import { numericFormat } from '@utils/index';
import Block from '@components/block';

interface OwnProps {
  label: string;
  showMaxAmount: boolean;
  coin: ParrotCoinStruct | undefined;
  disableCoins?: string | string[];
  onSelectCoin: (coinType: string) => void;
  amount: string | undefined;
  inputByUser: boolean;
  onChangeValue: (amount: string | undefined) => void;
  inputDisabled?: boolean;
  className?: string;
  fetchLoading: boolean;
}

/**
 * Scenarios:
 * - user input a
 * - user input b
 * - user click balance when input a
 * - user click balance when input b
 * - user input a and focus on b
 * - user input b and focus on a
 * - swap
 */
const SwapSection: FC<OwnProps> = (props) => {
  const {
    label,
    coin,
    disableCoins,
    onSelectCoin,
    showMaxAmount,
    amount,
    onChangeValue,
    inputByUser,
    inputDisabled,
    // onChangeAmount,
    className,
    fetchLoading,
  } = props;
  const { balance } = useParrotBalance(coin?.type);
  const [inputtingValue, setInputtingValue] = useState('');
  // const [focus, setFocus] = useState(false);

  useEffect(() => {
    if (amount && amount !== '' && coin) {
      setInputtingValue(scaleDown(amount, coin.decimals).toString());
    } else {
      setInputtingValue('');
    }
  }, [amount]);

  // const handleFocus = useCallback(() => {
  //   setFocus(true);
  // }, []);

  // const handleBlur = useCallback(() => {
  //   setFocus(false);
  // }, []);

  const handleChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      const { num, str } = filterPositiveNumber(e.target.value);
      setInputtingValue(str);
      if (num > 0) {
        onChangeValue(
          new Decimal(num)
            .mul(10 ** coin!.decimals)
            .floor()
            .toString(),
        );
      } else {
        onChangeValue(undefined);
      }
    },
    [onChangeValue, coin?.decimals],
  );

  const handleSetMax = useCallback(() => {
    if (!balance || !coin) return;
    setInputtingValue(scaleDown(balance, coin.decimals).toString());
    onChangeValue(balance);
  }, [balance, coin?.decimals, onChangeValue]);

  const handleSetHalf = useCallback(() => {
    if (!balance || !coin) return;
    const half = new Decimal(balance).div(2).floor().toString();
    setInputtingValue(scaleDown(half, coin.decimals).toString());
    onChangeValue(half);
  }, [balance, coin?.decimals, onChangeValue]);

  const loading = fetchLoading && !inputByUser;

  return (
    <div className={`${styles.wrapper} ${className}`}>
      <div className={styles.row}>
        <p className={styles.label}>{label}</p>
        {!!coin && (
          <p className={styles.max_number}>
            Balance:&nbsp;
            <span>{bigNumberToReadable(balance, coin.decimals)}</span>
            {showMaxAmount && (
              <>
                <span className={styles.half} onClick={handleSetHalf}>
                  Half
                </span>
                <span className={styles.max} onClick={handleSetMax}>
                  Max
                </span>
              </>
            )}
          </p>
        )}
      </div>
      <div className={styles.row}>
        <SelectToken disableCoins={disableCoins} onSelected={(coin) => onSelectCoin(coin.type)}>
          <div className={styles.select}>
            {coin ? (
              <>
                <CoinLogo src={coin.iconUrl || ''} className={styles.symbol_icon} />
                <div className={styles.symbol}>
                  <p>{coin.symbol}</p>
                </div>
              </>
            ) : (
              <div className={styles.symbol}>Select token</div>
            )}
            <img src={downIcon} />
          </div>
        </SelectToken>
        {loading ? (
          <Block height={24} />
        ) : (
          <Input
            placeholder="0"
            type="text"
            align="right"
            value={inputtingValue}
            wrapperClassName={styles.new_wrapper}
            className={`${styles.input}`}
            disabled={!coin || inputDisabled}
            onChange={handleChange}
            // onFocus={handleFocus}
            // onBlur={handleBlur}
          />
        )}
      </div>

      <div className={styles.usd}>
        <div>
          {coin?.price_usd
            ? `$${numericFormat(coin.price_usd.toString(), {
                decimalScale: 6,
              })}`
            : ''}
        </div>
        {loading ? (
          <Block height={12} width={68} />
        ) : (
          <div>
            ~ $
            {numericFormat(new Decimal(inputtingValue || 0).mul(coin?.price_usd || 0).toString(), {
              decimalScale: 6,
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default memo(SwapSection);
